// Day.js
import dayjs from "dayjs";
// FHIR
import {
  CodeableConcept,
  Coding,
  Identifier,
  Reference,
} from "fhir/r4";
// Translation
import i18n from "i18next";

export type StatusFlavor =
  | "success"
  | "info"
  | "error"
  | "warning"
  | "suspended"
  | "unknown";

export function formatDate(value?: string): string {
  if (!value) {
    return i18n.t("general.notavailable");
  }

  return dayjs(value).format("DD/MM/YYYY HH:mm");
}

export function formatPeriod(
  start?: string,
  end?: string,
): string {
  if (!start && !end) {
    return i18n.t("general.notavailable");
  }

  const values = [
    start ? formatDate(start) : undefined,
    end ? formatDate(end) : undefined,
  ].filter((value): value is string => Boolean(value));

  return values.join(" — ");
}

export function getReferenceValue(
  reference?: Reference,
): string {
  return (
    reference?.display ??
    reference?.identifier?.value ??
    reference?.reference ??
    i18n.t("general.notavailable")
  );
}

export function getReferenceIdentifier(
  reference?: Reference,
): string {
  const identifier = reference?.identifier;

  if (!identifier) {
    return i18n.t("general.notavailable");
  }

  const value = [
    identifier.value,
    identifier.system,
    identifier.assigner?.display,
  ]
    .filter((item): item is string => Boolean(item))
    .join(" — ");

  return value || i18n.t("general.notavailable");
}

export function getReferencePath(
  reference?: Reference,
): string {
  return (
    reference?.reference ??
    i18n.t("general.notavailable")
  );
}

export function getCodingValue(
  coding?: Coding,
): string {
  return (
    coding?.display ??
    coding?.code ??
    coding?.system ??
    i18n.t("general.notavailable")
  );
}

export function getCodeableConceptValue(
  concept?: CodeableConcept,
): string {
  return (
    concept?.text ??
    concept?.coding?.[0]?.display ??
    concept?.coding?.[0]?.code ??
    concept?.coding?.[0]?.system ??
    i18n.t("general.notavailable")
  );
}

export function getCodeableConceptValues(
  concepts?: CodeableConcept[],
): string[] {
  if (!concepts?.length) {
    return [i18n.t("general.notavailable")];
  }

  const values = concepts.flatMap((concept) => {
    const codingValues =
      concept.coding?.map((coding) =>
        getCodingValue(coding),
      ) ?? [];

    return concept.text
      ? [concept.text, ...codingValues]
      : codingValues;
  });

  const filteredValues = values.filter(
    (value) =>
      value !== i18n.t("general.notavailable"),
  );

  return filteredValues.length
    ? Array.from(new Set(filteredValues))
    : [i18n.t("general.notavailable")];
}

export function getIdentifierValues(
  identifiers?: Identifier[],
): string[] {
  if (!identifiers?.length) {
    return [i18n.t("general.notavailable")];
  }

  const values = identifiers
    .map((identifier) => {
      const type =
        identifier.type?.text ??
        identifier.type?.coding?.[0]?.display ??
        identifier.type?.coding?.[0]?.code;

      return [
        type,
        identifier.value,
        identifier.system,
        identifier.assigner?.display,
      ]
        .filter((item): item is string => Boolean(item))
        .join(" — ");
    })
    .filter(Boolean);

  return values.length
    ? Array.from(new Set(values))
    : [i18n.t("general.notavailable")];
}

export function getStatusFlavor(
  status?: string,
): StatusFlavor {
  switch (status) {
    case "active":
    case "arrived":
    case "booked":
    case "completed":
    case "finished":
    case "fulfilled":
      return "success";

    case "planned":
    case "pending":
    case "proposed":
    case "in-progress":
    case "onhold":
    case "onleave":
    case "waitlist":
    case "checked-in":
      return "suspended";

    case "triaged":
      return "warning";

    case "cancelled":
    case "entered-in-error":
    case "noshow":
      return "error";

    default:
      return "unknown";
  }
}

export function getNarrativeText(
  value?: string,
): string {
  if (!value) {
    return i18n.t("general.notavailable");
  }

  const document = new DOMParser().parseFromString(
    value,
    "text/html",
  );

  return (
    document.body.textContent?.trim() ||
    i18n.t("general.notavailable")
  );
}